import axios from 'axios';
import Cookies from 'js-cookie';
import {
  apiReachable,
  apiUnreachable,
  mutationUnconfirmed,
} from '../features/connection/connectionSlice';
import { clearPersistedKeys } from '../features/auth/clearPersistedKeys';
import { SESSION_END_REASON_KEY, SESSION_END_REASON } from '../features/auth/sessionEndReason';
import { isApiUnreachable, isSessionExpired, isTimedOut } from '../utils/errors';
import { markRequestElapsed, markRequestStart } from './requestTiming';

// To avoid circular dependency, we will inject the store dynamically or import it
let store;
export const injectStore = (_store) => {
  store = _store;
};

let persistor;
export const injectPersistor = (_persistor) => {
  persistor = _persistor;
};

const MUTATING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

const isSignedIn = () => Boolean(store?.getState().auth?.user);

/**
 * A change sent with a session open. Losing the answer to one of these does
 * not prove the API is down — it may have been applied — so it is reported in
 * place rather than by swapping the whole app for the offline screen. A POST
 * that only reads says so with `{ readOnly: true }` in its config.
 */
const isSignedInMutation = (config) =>
  MUTATING_METHODS.includes(config?.method?.toUpperCase()) &&
  !config?.readOnly &&
  config?.url !== 'login/' &&
  isSignedIn();

/**
 * Ends a session the server no longer accepts. Persisting is paused and its
 * queue drained before the sweep: the library writes on a timer, and a write
 * landing after it would put the previous account's state back.
 */
const endExpiredSession = () => {
  try {
    window.sessionStorage.setItem(SESSION_END_REASON_KEY, SESSION_END_REASON.EXPIRED);
  } catch {
    // A blocked storage only costs the explanation on the login screen.
  }
  persistor?.pause();
  persistor?.flush();
  clearPersistedKeys();
  store.dispatch({ type: 'auth/logoutLocal' });
  window.location.href = '/login';
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/',
  withCredentials: true,
  // Without this a dead backend leaves every request hanging until the browser
  // gives up on its own — minutes, depending on the OS — so the user stares at
  // a spinner long before anything tells them what is wrong.
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    markRequestStart(config);

    // Inject CSRF token for mutating requests
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase())) {
      const csrftoken = Cookies.get('csrftoken');
      if (csrftoken) {
        config.headers['X-CSRFToken'] = csrftoken;
      }
    }

    // Si la app usa JWT en el header además de cookies, podemos agregarlo aquí:
    const token = localStorage.getItem('api_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`; // O Bearer, dependiendo del backend de Django/DRF
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Any answer at all proves the API is alive, whatever it was answering.
    if (store) store.dispatch(apiReachable());
    return response;
  },
  (error) => {
    const { config } = markRequestElapsed(error);

    // A read that got no answer is the whole API, not this endpoint: the
    // screen has nothing to fall back on, so the app says so once and stops
    // pretending. A 500 deliberately does not land here — a broken view is one
    // broken view, and whoever asked for it can say so in its own box.
    // A screen that says `{ reportsNoAnswer: true }` reports the lost answer in
    // place and offers its own retry: the warehouse operator's, whose writes
    // carry an idempotency key, so retrying is safe and the generic "it may
    // have been applied" warning would be wrong.
    // A read that timed out proves nothing about the API either: the screen
    // keeps what it had and says it could not load the rest.
    if (store) {
      if (!isApiUnreachable(error)) store.dispatch(apiReachable());
      else if (config?.reportsNoAnswer) return Promise.reject(error);
      else if (isSignedInMutation(config)) store.dispatch(mutationUnconfirmed());
      else if (!isTimedOut(error)) store.dispatch(apiUnreachable());
    }

    // Only with somebody signed in: a deliberate logout has already cleared the
    // session, and its own late 401 is not an expiry. A 401 with a view's own
    // `{error}` is a refused permission, shown where it happened; the session
    // ends with DRF's own 401 on the next request, if it is dead.
    if (
      store &&
      config?.url !== 'login/' &&
      isSignedIn() &&
      error?.response?.status === 401 &&
      isSessionExpired(error)
    ) {
      endExpiredSession();
    }
    return Promise.reject(error);
  }
);

export default api;
